import { useState } from 'react';
import Swal from 'sweetalert2';
import type { AdminPost } from '@/mocks/adminPosts';
import { postTypeLabels, postTypeIcons, postTypeColors } from '@/mocks/adminPosts';
import { Link } from 'react-router-dom';

interface FeedPostCardProps {
  post: AdminPost;
  onEdit: (post: AdminPost) => void;
  onDelete: (post: AdminPost) => void;
  onToggleFeatured: (post: AdminPost) => void;
  onPublishNow: (post: AdminPost) => void;
  onShare: (post: AdminPost) => void;
  onViewResults: (post: AdminPost) => void;
  onClosePoll: (post: AdminPost) => void;
}

const RADIO_NAME = 'Radio Nueva Actitud';

function formatRelativeTime(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHrs < 24) return `Hace ${diffHrs} h`;
  if (diffDays < 7) return `Hace ${diffDays} d`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

export default function FeedPostCard({
  post,
  onEdit,
  onDelete,
  onToggleFeatured,
  onPublishNow,
  onShare,
  onViewResults,
  onClosePoll,
}: FeedPostCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const canPublishNow = post.status === 'borrador' || post.status === 'programado';
  const isPoll = post.type === 'encuesta' && post.pollData;
  const pollTotalVotes = post.pollData?.options.reduce((sum, o) => sum + o.votes, 0) || 0;

  const displayTime = post.status === 'programado' && post.scheduledDate
    ? post.scheduledDate
    : post.createdAt;

  const actualLikes = liked ? post.engagement.likes + 1 : post.engagement.likes;
  const actualComments = post.engagement.comments;
  const actualShares = post.engagement.shares;

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0 overflow-hidden">
          <span className="text-primary-700 font-bold text-sm">RNA</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-foreground-900 hover:underline cursor-pointer">{RADIO_NAME}</span>
            {post.isFeatured && (
              <i className="ri-star-fill text-amber-500 text-[11px]" title="Destacada"></i>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-400">{formatRelativeTime(displayTime)}</span>
            <span className="text-foreground-300">·</span>
            <i className="ri-earth-line text-xs text-foreground-400"></i>
            {post.isSponsored && (
              <>
                <span className="text-foreground-300">·</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-foreground-400">
                  <i className="ri-megaphone-line text-[9px]"></i>
                  Patrocinado
                </span>
              </>
            )}
          </div>
        </div>

        {/* Action menu */}
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer transition-colors"
          >
            <i className="ri-more-2-fill text-lg"></i>
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)}></div>
              <div className="absolute right-0 top-full mt-1 w-52 bg-background-50 rounded-xl border border-background-200 shadow-lg z-20 py-1">
                <button onClick={() => { setMenuOpen(false); onEdit(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors text-left">
                  <i className="ri-pencil-line text-base w-5 text-center"></i> Editar
                </button>
                <button onClick={() => { setMenuOpen(false); onToggleFeatured(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors text-left">
                  <i className={`text-base w-5 text-center ${post.isFeatured ? 'ri-star-line text-amber-500' : 'ri-star-fill text-amber-500'}`}></i>
                  {post.isFeatured ? 'Quitar destacada' : 'Marcar destacada'}
                </button>
                <button onClick={() => { setMenuOpen(false); onShare(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors text-left">
                  <i className="ri-share-forward-line text-base w-5 text-center"></i> Compartir
                </button>
                {canPublishNow && (
                  <button onClick={() => { setMenuOpen(false); onPublishNow(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 cursor-pointer transition-colors text-left">
                    <i className="ri-send-plane-fill text-base w-5 text-center"></i> Publicar ahora
                  </button>
                )}
                {isPoll && (
                  <button onClick={() => { setMenuOpen(false); onViewResults(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-600 hover:bg-amber-50 cursor-pointer transition-colors text-left">
                    <i className="ri-bar-chart-2-line text-base w-5 text-center"></i> Ver resultados
                  </button>
                )}
                {isPoll && (
                  <button onClick={() => { setMenuOpen(false); onClosePoll(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors text-left">
                    <i className={`text-base w-5 text-center ${post.pollData!.isClosed ? 'ri-lock-unlock-line' : 'ri-lock-line'}`}></i>
                    {post.pollData!.isClosed ? 'Reabrir encuesta' : 'Cerrar encuesta'}
                  </button>
                )}
                <div className="border-t border-background-200 my-1"></div>
                <button onClick={() => { setMenuOpen(false); onDelete(post); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer transition-colors text-left">
                  <i className="ri-delete-bin-line text-base w-5 text-center"></i> Eliminar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Sponsored Label ── */}
      {post.isSponsored && post.sponsorName && (
        <div className="px-4 pb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-50 text-primary-700 text-[10px] font-semibold">
            <i className="ri-megaphone-line text-[9px]"></i>
            Patrocinado · {post.sponsorName}
          </span>
          {post.engagement.reach > 0 && (
            <span className="ml-2 text-[10px] text-foreground-400">
              Alcance: {formatCount(post.engagement.reach)}
            </span>
          )}
        </div>
      )}

      {/* ── Status & Type badges (for admin context) ── */}
      <div className="px-4 pb-2 flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${postTypeColors[post.type]}`}>
          <i className={`${postTypeIcons[post.type]} text-[9px]`}></i>
          {postTypeLabels[post.type]}
        </span>
        {post.derivedFromLiveId && (
          <Link to="/admin/live" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 hover:bg-red-200 cursor-pointer transition-colors">
            <i className="ri-live-line text-[9px]"></i>
            Live #{post.derivedFromLiveId}
          </Link>
        )}
        {post.derivedFromVideoId && (
          <Link to="/admin/videos" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer transition-colors">
            <i className="ri-video-line text-[9px]"></i>
            Video #{post.derivedFromVideoId}
          </Link>
        )}
        {post.status === 'programado' && post.scheduledDate && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
            <i className="ri-time-line text-[9px]"></i>
            {new Date(post.scheduledDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        {post.status === 'borrador' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-foreground-100 text-foreground-500">
            <i className="ri-draft-line text-[9px]"></i>
            Borrador
          </span>
        )}
      </div>

      {/* ── Text content ── */}
      {post.text && (
        <div className="px-4 pb-3">
          <p className="text-sm text-foreground-800 leading-relaxed whitespace-pre-line">{post.text}</p>
        </div>
      )}

      {/* ── Image ── */}
      {post.imageUrl && (post.type === 'imagen' || post.type === 'patrocinada') && (
        <div className="w-full">
          <img
            src={post.imageUrl}
            alt={post.title}
            className="w-full object-cover object-top"
            style={{ maxHeight: '480px' }}
          />
        </div>
      )}

      {/* ── Video ── */}
      {post.type === 'video' && post.imageUrl && (
        <div className="relative w-full cursor-pointer group">
          <img
            src={post.imageUrl}
            alt={post.title}
            className="w-full object-cover object-top"
            style={{ maxHeight: '480px' }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-foreground-950/60 flex items-center justify-center group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-200">
              <i className="ri-play-fill text-background-50 text-2xl ml-0.5"></i>
            </div>
          </div>
        </div>
      )}

      {/* ── Poll ── */}
      {isPoll && post.pollData && (
        <div className="px-4 pb-3">
          <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-200 space-y-3">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                post.pollData.isClosed ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                <i className={`text-[9px] ${post.pollData.isClosed ? 'ri-lock-line' : 'ri-checkbox-circle-line'}`}></i>
                {post.pollData.isClosed ? 'Cerrada' : 'Abierta'}
              </span>
              <span className="text-[10px] text-foreground-400 font-medium">
                {pollTotalVotes.toLocaleString()} votos · {post.pollData.pollType === 'single' ? 'Una respuesta' : 'Varias respuestas'}
              </span>
            </div>
            <div className="space-y-1.5">
              {post.pollData.options.map((opt) => {
                const pct = pollTotalVotes > 0 ? Math.round((opt.votes / pollTotalVotes) * 100) : 0;
                return (
                  <div key={opt.id} className="relative">
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-background-50 border border-background-200">
                      <span className="text-xs font-medium text-foreground-700">{opt.text}</span>
                      <span className="text-[11px] font-bold text-foreground-500">{pct}%</span>
                    </div>
                    <div
                      className="absolute inset-y-1 left-1 rounded-md bg-amber-400/30 transition-all duration-500"
                      style={{ width: `calc(${pct}% - 4px)` }}
                    ></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Engagement bar ── */}
      <div className="px-4 py-2">
        {/* Counts row */}
        {post.status === 'publicado' && (actualLikes > 0 || actualComments > 0 || actualShares > 0) && (
          <div className="flex items-center justify-between pb-2.5">
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-thumb-up-fill text-primary-600 text-[9px]"></i>
              </span>
              {liked && <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-heart-fill text-red-500 text-[9px]"></i>
              </span>}
              <span className="text-xs text-foreground-400">{formatCount(actualLikes)}</span>
            </div>
            <div className="flex items-center gap-3">
              {actualComments > 0 && (
                <span className="text-xs text-foreground-400 hover:underline cursor-pointer">{formatCount(actualComments)} comentarios</span>
              )}
              {actualShares > 0 && (
                <span className="text-xs text-foreground-400 hover:underline cursor-pointer">{formatCount(actualShares)} veces compartido</span>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className={`flex items-center ${(post.status === 'publicado' && (actualLikes > 0 || actualComments > 0 || actualShares > 0)) ? 'border-t border-background-200 pt-1.5' : ''}`}>
          <button
            onClick={() => setLiked(!liked)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              liked ? 'text-primary-600 bg-primary-50' : 'text-foreground-400 hover:bg-background-100'
            }`}
          >
            <i className={`text-sm ${liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}`}></i>
            Me gusta
          </button>
          <button
            onClick={() => Swal.fire({ toast: true, position: 'top-end', title: 'Comentarios visibles en la página pública', icon: 'info', showConfirmButton: false, timer: 2000, timerProgressBar: true, customClass: { popup: 'rounded-xl' } })}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-foreground-400 hover:bg-background-100 cursor-pointer transition-colors">
            <i className="ri-chat-3-line text-sm"></i>
            Comentar
          </button>
          <button
            onClick={() => onShare(post)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-foreground-400 hover:bg-background-100 cursor-pointer transition-colors"
          >
            <i className="ri-share-forward-line text-sm"></i>
            Compartir
          </button>
        </div>
      </div>
    </div>
  );
}